import { api, type ListResponse } from '@/api/client'

export type AiConversation = {
  id: number
  user_id: number
  title: string | null
  route_context: string | null
  last_context_tokens: number | null
  created_at: string
  updated_at: string | null
}

export type AttachmentMeta = {
  id: number
  filename: string
  mime: string
  kind: 'image' | 'text'
  size: number
}

export type AiSearchResult = {
  id: string
  type: string
  name: string
  description: string
  status?: string
  resource_ref: { type: string; id: string | number; label?: string }
  score: number
}

export type AiModelInfo = {
  id: string
  label: string
  description: string
}

export type AiStatus = {
  enabled: boolean
  openapi_ok?: boolean
  unavailable_reason?: string
  models?: AiModelInfo[]
  default_model?: string
  context_window?: number
}

export type PendingApproval = {
  tool_use_id: string
  method: string
  path: string
  args: { query?: Record<string, unknown>; body?: unknown }
  summary: string
  rememberable?: boolean
}

export type AskUserQuestionOption = {
  id: string
  label: string
  description?: string
}

export type AskUserQuestion = {
  id: string
  prompt: string
  selection?: 'single' | 'multiple'
  allow_custom?: boolean
  options: AskUserQuestionOption[]
}

export type AskUserAnswer = {
  question_id: string
  selected_option_ids: string[]
  custom_text?: string
}

export type PendingUserQuestions = {
  tool_use_id: string
  title?: string
  questions: AskUserQuestion[]
}

export type AiCompactResult = {
  status: 'done' | 'skipped' | 'failed'
  message_id?: number
  summary?: string
  message?: string
  context_tokens?: number
}

/** Durable turn still in flight or paused for the user (from GET conversation). */
export type ActiveTurn = {
  id: number
  status: 'running' | 'waiting_approval' | 'waiting_user' | 'succeeded' | 'failed' | 'cancelled'
  last_event_seq: number
}

async function uploadAttachment(file: File): Promise<AttachmentMeta> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch('/api/ai/attachments', { method: 'POST', credentials: 'include', body: fd })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string }
    throw new Error(body.message || 'Upload failed')
  }
  return res.json() as Promise<AttachmentMeta>
}

function attachmentUrl(id: number) {
  return `/api/ai/attachments/${id}`
}

function deleteAttachment(id: number) {
  return api.delete<{ success: boolean }>(`/ai/attachments/${id}`)
}

export const aiApi = {
  status: () => api.get<AiStatus>('/ai/status'),
  search: (q: string, types?: string[]) => {
    const params = new URLSearchParams({ q })
    if (types?.length) params.set('types', types.join(','))
    return api.get<{ results: AiSearchResult[] }>(`/ai/search?${params}`)
  },
  listConversations: (params?: { q?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.q) qs.set('q', params.q)
    if (params?.limit) qs.set('limit', String(params.limit))
    const query = qs.toString()
    return api.get<ListResponse<AiConversation>>(`/ai/conversations${query ? `?${query}` : ''}`)
  },
  getConversation: (id: number) =>
    api.get<{
      conversation: AiConversation
      messages: Array<{ id: number; role: string; content: string; segments?: string | null; seq: number }>
      active_turn?: ActiveTurn | null
      pending?: {
        approval: PendingApproval | null
        user_questions: PendingUserQuestions | null
      }
    }>(
      `/ai/conversations/${id}`,
    ),
  renameConversation: (id: number, title: string) =>
    api.patch<AiConversation>(`/ai/conversations/${id}`, { title }),
  compactConversation: (id: number, signal?: AbortSignal) =>
    api.post<AiCompactResult>(`/ai/conversations/${id}/compact`, {}, { signal }),
  deleteConversation: (id: number) =>
    api.delete<{ success: boolean }>(`/ai/conversations/${id}`),
  /** Re-attach SSE tail for a durable turn. `after` is exclusive (events with seq > after). */
  streamTurn: (id: number, after = 0, signal?: AbortSignal) =>
    fetch(`/api/ai/turns/${id}/stream?after=${encodeURIComponent(String(after))}`, {
      method: 'GET',
      credentials: 'include',
      signal,
    }),
  cancelTurn: (id: number) =>
    api.post<{ success: boolean; id: number; status: 'cancelled' }>(`/ai/turns/${id}/cancel`),
  uploadAttachment,
  attachmentUrl,
  deleteAttachment,
}
